// client/src/pages/Game/Battlefield/components/ActionButton.jsx

import React from 'react';
import './ActionButton.css';

/**
 * Composant pour le bouton d'action/passer
 * @param {Object} props Les propriétés du composant
 * @param {Object} props.gameState État actuel du jeu
 * @param {Object} props.position Position {x, y} du bouton
 * @param {Object} props.size Taille {width, height} du bouton
 * @param {Function} props.onAction Fonction appelée pour valider une action ou passer
 * @param {Boolean} props.loading Indique si une action est en cours
 * @param {Function} props.onDevDragStart Fonction appelée en mode développeur pour déplacer le bouton
 * @param {Boolean} props.devMode Si le mode développeur est activé
 * @returns {JSX.Element} Le composant ActionButton
 */
const ActionButton = ({
  gameState,
  position,
  size,
  onAction,
  loading,
  onDevDragStart,
  devMode
}) => {
  if (!gameState) return null;
  
  // Déterminer si c'est le tour du joueur
  const isPlayerTurn = gameState.activePlayer === 'player';
  
  // Vérifier si un monstre est déjà dans la zone de bataille (prêt à attaquer)
  const hasMonstersInBattleZone = gameState?.playerField?.some(card => card.zone === 'battle');
  
  // Fonction pour obtenir le texte du bouton en fonction du contexte de jeu
  const getButtonText = () => {
    if (loading) return "Chargement...";
    
    // Si ce n'est pas le tour du joueur
    if (!isPlayerTurn) return "Tour de l'adversaire";
    
    // Si le joueur est en phase de mulligan
    if (gameState.phase === 'mulligan') return "Confirmer mulligan";

    // Fenêtre de réaction (l'adversaire a défendu) : magie rapide possible,
    // le bouton continue/résout le combat. pendingAction est une CHAÎNE
    // côté serveur ('react', 'defend', …) — gérer les deux formes.
    const pa = gameState.pendingAction;
    if (pa === 'react') return "Continuer";
    if (pa === 'defend') return "Confirmer la défense";
    
    // Si le joueur a une action en attente à confirmer (forme objet héritée)
    if (pa && typeof pa === 'object') {
      switch(pa.type) {
        case 'SUMMON':
          return "Confirmer l'invocation";
        case 'PREPARE_ATTACK':
          return "Confirmer l'attaque";
        case 'PREPARE_DEFENSE':
          return "Confirmer la défense";
        case 'PREPARE_ATTACK_RESPONSE':
          return "Résoudre le combat";
        default:
          return "Confirmer action";
      }
    }
    
    // Si nous sommes en mode défense (IA attaque et joueur défend)
    if (gameState.pendingAttack && gameState.pendingAttack.attackers && 
        gameState.pendingAttack.attackers.length > 0 && !gameState.playerHasAttack) {
      return "Défendre";
    }
    
    // Si nous sommes en phase de bataille ET si des monstres sont déjà en zone de bataille
    if (gameState.phase === 'battle' && gameState.playerHasAttack && 
        gameState.playerField.some(card => card.zone === 'battle')) {
      // Au lieu de "Terminer mon attaque" qui pourrait être interprété comme une annulation
      return "Confirmer l'attaque";
    }
    
    // Pour les cas où le joueur a le jeton d'attaque et des monstres sur le terrain
    // Vérifier explicitement qu'il a des monstres pouvant attaquer
    if (gameState.playerHasAttack && 
        gameState.playerField.some(card => 
          card.zone === 'monster' && !card.hasAttacked)) {
      return "Passer";
    }
    
    // Pour tous les autres cas
    return "Passer";
  }  
  
  // Déterminer la classe CSS à utiliser pour le bouton en fonction de l'état du jeu
  const getButtonClass = () => {
    const baseClass = 'action-button';
    
    // Si ce n'est pas le tour du joueur, style standard
    if (!isPlayerTurn) {
      return baseClass;
    }
    
    // Si une action est en attente de confirmation
    if (gameState.pendingAction) {
      return `${baseClass} awaiting-confirmation`;
    }
    
    // Si des monstres sont déjà en zone de bataille (prêts à attaquer)
    if (hasMonstersInBattleZone) {
      return `${baseClass} attack-button`;
    }
    
    // Si le joueur n'a pas le jeton d'attaque (peut défendre)
    if (!gameState.playerHasAttack && gameState.opponentHasAttack) {
      return `${baseClass} defense-button`;
    }
    
    // Cas standard
    return baseClass;
  }
  
  // Gestionnaire pour le drag en mode développeur
  const handleDevDrag = (e) => {
    if (devMode && onDevDragStart) {
      onDevDragStart(e);
    }
  };
  
  // Gestionnaire de clic pour le bouton d'action
  const handleButtonClick = () => {
    if (!isPlayerTurn || loading || devMode) return;
    
    console.log("Bouton cliqué, action en cours:", gameState.pendingAction);
    
    // Cas spécial: Si nous sommes en défense (l'adversaire a le jeton d'attaque et des attaquants)
    // mais que nous n'avons pas placé de défenseurs, permettre quand même de confirmer
    if (gameState.pendingAttack && 
        gameState.pendingAttack.attackers && 
        gameState.pendingAttack.attackers.length > 0 &&
        !gameState.playerHasAttack && 
        gameState.opponentHasAttack) {
      console.log("Confirmation de défense sans défenseurs (attaque directe)");
    
      // Forcer l'envoi d'une action CONFIRM_ACTION pour résoudre l'attaque directe
      if (onAction && typeof onAction === 'function') {
        onAction();
      }
      return;
    }
    
    if (onAction && typeof onAction === 'function') {
      onAction();
    } else {
      console.error("La fonction onAction n'est pas définie ou n'est pas une fonction");
    }
  };
  
  // Déterminer si le bouton doit être désactivé
  const isButtonDisabled = !isPlayerTurn || loading || devMode;
  
  return (
    <div 
      className="game-element action-button-container"
      style={{ 
        left: `${position.x}%`,
        top: `${position.y}%`,
        // R34q : largeur adaptée au texte (le conteneur 20cqw comprimait le
        // bouton et tronquait « Confirmer l'attaque »). En mode dev, on garde
        // la largeur calibrable pour le DevPanel.
        width: devMode ? `${size.width}cqw` : 'max-content',
        height: `${size.height}cqw`,
        transform: 'translate(-50%, -50%)',
        cursor: devMode ? 'move' : 'default',
      }}
      onMouseDown={devMode ? handleDevDrag : undefined}
      onTouchStart={devMode ? handleDevDrag : undefined}
    >
      <div className="turn-info"></div>
      
      <button 
        className={getButtonClass()}
        onClick={!devMode ? handleButtonClick : undefined}
        disabled={isButtonDisabled}
      >
        {getButtonText()}
      </button>
    </div>
  );
};

export default ActionButton;